import axiosInstance from './axiosInstance'

const TENANT_BASE = '/tenants'

// Cache en memoria para evitar llamadas repetidas
let tenantsCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Obtiene todos los tenants habilitados
 * Con cache en memoria para evitar llamadas repetidas
 */
export const fetchEnabledTenants = async () => {
  const now = Date.now()

  if (tenantsCache && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
    return tenantsCache
  }

  try {
    const response = await axiosInstance.get(`${TENANT_BASE}/enabled`)
    tenantsCache = response.data.tenants
    cacheTimestamp = now
    return tenantsCache
  } catch (error) {
    console.error('Error fetching tenants:', error)
    return tenantsCache || []
  }
}

/**
 * Obtiene un tenant por su slug
 */
export const fetchTenantBySlug = async slug => {
  // Primero buscar en cache
  if (tenantsCache) {
    const cached = tenantsCache.find(t => t.slug === slug)
    if (cached) return cached
  }

  try {
    const response = await axiosInstance.get(`${TENANT_BASE}/${slug}`)
    return response.data.tenant
  } catch (error) {
    console.error(`Error fetching tenant ${slug}:`, error)
    return null
  }
}

/**
 * Obtiene el tenant por defecto
 */
export const fetchDefaultTenant = async () => {
  try {
    const response = await axiosInstance.get(`${TENANT_BASE}/default`)
    return response.data.tenant
  } catch (error) {
    console.error('Error fetching default tenant:', error)
    return null
  }
}

/**
 * Obtiene el boundary GeoJSON de un tenant
 */
export const fetchTenantBoundary = async (slug, options = {}) => {
  const { noCache = false, throwOnError = false } = options

  const requestConfig = {}
  if (noCache) {
    requestConfig.headers = {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    }
    requestConfig.params = { _ts: Date.now() }
  }

  try {
    const response = await axiosInstance.get(`${TENANT_BASE}/${slug}/boundary`, requestConfig)
    return response.data
  } catch (error) {
    console.error(`Error fetching boundary for tenant ${slug}:`, error)
    if (throwOnError) throw error
    return null
  }
}

/**
 * Obtiene lista de slugs válidos
 */
export const fetchValidSlugs = async () => {
  try {
    const response = await axiosInstance.get(`${TENANT_BASE}/slugs`)
    return response.data.slugs
  } catch (error) {
    console.error('Error fetching valid slugs:', error)
    return []
  }
}

/**
 * Invalida el cache (llamar cuando se actualizan tenants)
 */
export const invalidateTenantsCache = () => {
  tenantsCache = null
  cacheTimestamp = null
}

/**
 * Obtiene todos los tenants (incluye habilitados y deshabilitados)
 * Para uso en panel de administración
 */
export const fetchAllTenants = async () => {
  try {
    const response = await axiosInstance.get(TENANT_BASE)
    return response.data.tenants || []
  } catch (error) {
    console.error('Error fetching all tenants:', error)
    return []
  }
}

/**
 * Crea un nuevo tenant
 * @param {Object} data - Datos del tenant
 */
export const createTenant = async data => {
  try {
    const response = await axiosInstance.post(TENANT_BASE, data)
    invalidateTenantsCache()
    return { success: true, tenant: response.data.tenant }
  } catch (error) {
    console.error('Error creating tenant:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al crear el tenant',
    }
  }
}

/**
 * Actualiza un tenant existente
 * @param {number} id - ID del tenant
 * @param {Object} data - Datos a actualizar
 */
export const updateTenant = async (id, data) => {
  try {
    const response = await axiosInstance.put(`${TENANT_BASE}/${id}`, data)
    invalidateTenantsCache()
    return { success: true, tenant: response.data.tenant }
  } catch (error) {
    console.error('Error updating tenant:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al actualizar el tenant',
    }
  }
}

/**
 * Habilita un tenant
 * @param {number} id - ID del tenant
 */
export const enableTenant = async id => {
  try {
    const response = await axiosInstance.patch(`${TENANT_BASE}/${id}/enable`)
    invalidateTenantsCache()
    return { success: true, tenant: response.data.tenant }
  } catch (error) {
    console.error('Error enabling tenant:', error)
    return { success: false, message: 'Error al habilitar el tenant' }
  }
}

/**
 * Deshabilita un tenant
 * @param {number} id - ID del tenant
 */
export const disableTenant = async id => {
  try {
    const response = await axiosInstance.patch(`${TENANT_BASE}/${id}/disable`)
    invalidateTenantsCache()
    return { success: true, tenant: response.data.tenant }
  } catch (error) {
    console.error('Error disabling tenant:', error)
    return { success: false, message: 'Error al deshabilitar el tenant' }
  }
}
/**
 * Elimina un tenant
 * @param {number} id - ID del tenant
 */
export const deleteTenant = async id => {
  try {
    await axiosInstance.delete(`${TENANT_BASE}/${id}`)
    invalidateTenantsCache()
    return { success: true, message: 'Tenant eliminado correctamente' }
  } catch (error) {
    console.error('Error deleting tenant:', error)
    return { success: false, message: 'Error al eliminar el tenant' }
  }
}

/**
 * Reasigna los árboles dentro del boundary_geojson del tenant al tenant indicado.
 * @param {number} tenantId - ID del tenant
 */
export const reassignTreesForTenant = async tenantId => {
  try {
    const response = await axiosInstance.post(`${TENANT_BASE}/${tenantId}/reassign-trees`, null, { timeout: 120000 })
    return { success: true, data: response.data }
  } catch (error) {
    console.error('Error reassigning trees for tenant:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al reasignar los árboles',
    }
  }
}
